import { skipToken } from '@apollo/client'
import { format, subMonths } from 'date-fns'
import uniqBy from 'lodash/uniqBy'
import { BarChart3, Printer } from 'lucide-react'
import { useMemo, useState } from 'react'
import { QueryErrorBoundary } from '@/components/shared/error-boundary-overlay/query-error-boundary'
import { UsageAlertCard } from '@/components/shared/usage-alert-card'
import { UsageAlertCardFallback } from '@/components/shared/usage-alert-card/fallback'
import { USAGE_ALERT_CONFIG_QUERY } from '@/components/shared/usage-alert-card/query'
import { H3, Muted } from '@/components/sv/typographies'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { useIdCompany, useRBAC, useSuspenseQuery } from '@/lib/hooks'
import { EmptySummaryCards } from '@/routes/usage/components/empty-summary-cards'
import { EmptyUsageChart } from '@/routes/usage/components/empty-usage-chart'
import { EmptyUsageTable } from '@/routes/usage/components/empty-usage-table'
import { SummaryCards } from '@/routes/usage/components/summary-cards'
import { UsageChart } from '@/routes/usage/components/usage-chart'
import { UsageFilters } from '@/routes/usage/components/usage-filters'
import { UsageTable } from '@/routes/usage/components/usage-table'
import { UsageTableActions } from '@/routes/usage/components/usage-table-actions'
import {
  PERIOD_OPTIONS,
  USAGE_TYPE_TO_SERVICE_TYPE,
} from '@/routes/usage/constants'
import {
  BILLING_DAILY_USAGE_REPORT_QUERY,
  BILLING_USAGE_CONTEXT_QUERY,
} from '@/routes/usage/queries'
import {
  aggregateUsageRows,
  computeProjection,
  filterDailyReport,
  groupUsageRows,
} from '@/routes/usage/utils'
import type {
  BillingDailyUsageReportQuery,
  BillingDailyUsageReportQueryVariables,
  BillingUsageContextQuery,
  BillingUsageContextQueryVariables,
} from '@/routes/usage/queries.types.gen'
import type {
  ProjectInfo,
  ServiceInfo,
  UsageFilterState,
} from '@/routes/usage/types'

const toDate = (value: Date | string | number | null | undefined) => {
  if (value === null || value === undefined) {
    return null
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export const UsageContent = () => {
  const { idCompany } = useIdCompany()
  const { rbac } = useRBAC()
  const canViewUsage = rbac.company(idCompany).usage.view.can()

  const [filters, setFilters] = useState<UsageFilterState>({
    period: 'current',
    selectedProjects: [],
    selectedServices: [],
    selectedTypes: [],
  })
  const [highlightedType, setHighlightedType] = useState<string | null>(null)

  const { data: contextData } = useSuspenseQuery<
    BillingUsageContextQuery,
    BillingUsageContextQueryVariables
  >(BILLING_USAGE_CONTEXT_QUERY, {
    variables: { idCompany },
  })

  const subscription = contextData?.company?.sevallaBilling?.subscription
  const currentPeriodStart = toDate(subscription?.currentPeriodStart)
  const currentPeriodEnd = toDate(subscription?.currentPeriodEnd)

  const previousPeriodStart = useMemo(
    () => (currentPeriodStart ? subMonths(currentPeriodStart, 1) : null),
    [currentPeriodStart]
  )
  const previousPeriodEnd = useMemo(
    () => (currentPeriodEnd ? subMonths(currentPeriodEnd, 1) : null),
    [currentPeriodEnd]
  )

  const selectedPeriodStart =
    filters.period === 'previous' ? previousPeriodStart : currentPeriodStart
  const selectedPeriodEnd =
    filters.period === 'previous' ? previousPeriodEnd : currentPeriodEnd

  const { data: currentDailyData } = useSuspenseQuery<
    BillingDailyUsageReportQuery,
    BillingDailyUsageReportQueryVariables
  >(
    BILLING_DAILY_USAGE_REPORT_QUERY,
    !currentPeriodStart || !currentPeriodEnd
      ? skipToken
      : {
          variables: {
            idCompany,
            startDate: currentPeriodStart,
            endDate: currentPeriodEnd,
            hasPermission: canViewUsage,
          },
        }
  )

  const { data: previousDailyData } = useSuspenseQuery<
    BillingDailyUsageReportQuery,
    BillingDailyUsageReportQueryVariables
  >(
    BILLING_DAILY_USAGE_REPORT_QUERY,
    !previousPeriodStart || !previousPeriodEnd
      ? skipToken
      : {
          variables: {
            idCompany,
            startDate: previousPeriodStart,
            endDate: previousPeriodEnd,
            hasPermission: canViewUsage,
          },
        }
  )

  const currentReport =
    currentDailyData?.company?.dailyResourceUsageReport ?? null
  const previousReport =
    previousDailyData?.company?.dailyResourceUsageReport ?? null

  const serviceDisplayNameMap = useMemo(() => {
    const map = new Map<string, string>()
    const company = contextData?.company
    const allServices = [
      ...(company?.allApps?.items ?? []),
      ...(company?.allDatabases?.items ?? []),
      ...(company?.allEdgeLoadBalancers?.items ?? []),
      ...(company?.allObjectStorages?.items ?? []),
      ...(company?.allStaticSites?.items ?? []),
    ]
    for (const svc of allServices) {
      map.set(svc.id, svc.displayName)
    }
    return map
  }, [contextData])

  const { projects, services, serviceToProject } = useMemo(() => {
    const projectItems = contextData?.company?.projects?.items ?? []
    const projectList: ProjectInfo[] = []
    const serviceList: ServiceInfo[] = []
    const svcToProject = new Map<string, string>()

    for (const project of projectItems) {
      projectList.push({ id: project.id, displayName: project.displayName })

      const typedServices: {
        svc: { id: string; displayName: string }
        serviceType: ServiceInfo['serviceType']
      }[] = [
        ...(project.apps ?? []).map((s) => ({
          svc: s,
          serviceType: 'app' as const,
        })),
        ...(project.databases ?? []).map((s) => ({
          svc: s,
          serviceType: 'db' as const,
        })),
        ...(project.edgeLoadBalancers ?? []).map((s) => ({
          svc: s,
          serviceType: 'loadBalancer' as const,
        })),
        ...(project.objectStorages ?? []).map((s) => ({
          svc: s,
          serviceType: 'objectStorage' as const,
        })),
        ...(project.staticSites ?? []).map((s) => ({
          svc: s,
          serviceType: 'staticSite' as const,
        })),
      ]

      for (const { svc, serviceType } of typedServices) {
        serviceList.push({
          id: svc.id,
          displayName: svc.displayName,
          idProject: project.id,
          serviceType,
        })
        svcToProject.set(svc.id, project.id)
      }
    }

    const knownServiceIds = new Set(serviceList.map((s) => s.id))
    const reports = [currentReport, previousReport]
    for (const report of reports) {
      if (!report) {
        continue
      }
      for (const day of report.days) {
        for (const svc of day.services) {
          if (svc.idService && !knownServiceIds.has(svc.idService)) {
            knownServiceIds.add(svc.idService)
            const firstUsageType = svc.usageByType[0]?.type
            const inferredType = firstUsageType
              ? ((USAGE_TYPE_TO_SERVICE_TYPE[
                  firstUsageType
                ] as ServiceInfo['serviceType']) ?? null)
              : null
            serviceList.push({
              id: svc.idService,
              displayName:
                serviceDisplayNameMap.get(svc.idService) ||
                svc.serviceName ||
                svc.idService,
              idProject: '',
              serviceType: inferredType,
            })
          }
        }
      }
    }

    return {
      projects: projectList,
      services: uniqBy(serviceList, 'id'),
      serviceToProject: svcToProject,
    }
  }, [contextData, currentReport, previousReport, serviceDisplayNameMap])

  const { allUsageTypes, serviceIdsWithUsage } = useMemo(() => {
    const types = new Set<string>()
    const svcIds = new Set<string>()
    const reports = [currentReport, previousReport]
    for (const report of reports) {
      if (!report) {
        continue
      }
      for (const day of report.days) {
        for (const svc of day.services) {
          if (svc.idService && svc.usageByType.length > 0) {
            svcIds.add(svc.idService)
          }
          for (const u of svc.usageByType) {
            types.add(u.type)
          }
        }
      }
    }
    return {
      allUsageTypes: Array.from(types).sort(),
      serviceIdsWithUsage: svcIds,
    }
  }, [currentReport, previousReport])

  const selectedReport =
    filters.period === 'previous' ? previousReport : currentReport
  const otherReport =
    filters.period === 'previous' ? currentReport : previousReport

  const filteredReport = useMemo(
    () =>
      filterDailyReport({
        report: selectedReport,
        selectedServices: filters.selectedServices,
        selectedTypes: filters.selectedTypes,
        serviceToProject,
        selectedProjects: filters.selectedProjects,
      }),
    [
      selectedReport,
      filters.selectedServices,
      filters.selectedTypes,
      filters.selectedProjects,
      serviceToProject,
    ]
  )

  const filteredPreviousReport = useMemo(
    () =>
      filterDailyReport({
        report: otherReport,
        selectedServices: filters.selectedServices,
        selectedTypes: filters.selectedTypes,
        serviceToProject,
        selectedProjects: filters.selectedProjects,
      }),
    [
      otherReport,
      filters.selectedServices,
      filters.selectedTypes,
      filters.selectedProjects,
      serviceToProject,
    ]
  )

  const projection = useMemo(() => {
    if (filters.period === 'previous' || !filteredReport || !currentPeriodEnd) {
      return { projectedTotal: 0, projectedDailyAmount: 0 }
    }

    return computeProjection({
      days: filteredReport.days,
      periodEnd: currentPeriodEnd,
    })
  }, [filteredReport, filters.period, currentPeriodEnd])

  // Aggregated rows for table
  const tableRows = useMemo(
    () => aggregateUsageRows({ report: filteredReport }),
    [filteredReport]
  )

  // Grouped by usage type for expandable table
  const tableGroups = useMemo(() => groupUsageRows(tableRows), [tableRows])

  const currentTotal = filteredReport?.totalGross ?? 0
  const previousTotal = filteredPreviousReport?.totalGross ?? 0
  const projectedTotal =
    filters.period === 'previous' ? currentTotal : projection.projectedTotal
  const isPreviousPeriod = filters.period === 'previous'

  const periodLabel =
    PERIOD_OPTIONS.find((o) => o.value === filters.period)?.label ??
    'Current period'

  const hasUsageData =
    currentReport !== null &&
    currentReport.days.some((day) =>
      day.services.some((svc) => svc.usageByType.length > 0)
    )

  const alertSpendUsd = currentReport?.totalGross ?? 0

  return (
    <div className="space-y-6">
      <Card className="bg-secondary p-0 gap-0">
        <CardHeader className="px-5 py-3 flex flex-row items-center justify-between gap-0">
          <CardTitle>
            <H3 className="flex items-center gap-2 text-sm text-muted-foreground">
              <BarChart3 className="size-4" /> Usage
            </H3>
          </CardTitle>
          {selectedPeriodStart && selectedPeriodEnd && (
            <Muted className="text-sm tabular-nums">
              {format(selectedPeriodStart, 'MMM dd, yyyy')} &ndash;{' '}
              {format(selectedPeriodEnd, 'MMM dd, yyyy')}
            </Muted>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Card className="p-0 border-x-0 border-b-0 shadow-none">
            <CardContent className="p-0">
              {hasUsageData ? (
                <>
                  <div className="px-6 py-5 flex items-center justify-between gap-4 print:hidden">
                    <UsageFilters
                      filters={filters}
                      onFiltersChange={setFilters}
                      projects={projects}
                      services={services.filter((s) =>
                        serviceIdsWithUsage.has(s.id)
                      )}
                      usageTypes={allUsageTypes}
                    />
                    <div className="flex items-center gap-2">
                      <UsageTableActions
                        groups={tableGroups}
                        periodLabel={periodLabel}
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 gap-1.5 text-sm"
                        onClick={() => window.print()}
                      >
                        <Printer className="size-3.5" />
                        Print
                      </Button>
                    </div>
                  </div>
                  <Separator />
                  <SummaryCards
                    currentTotal={currentTotal}
                    projectedTotal={projectedTotal}
                    previousTotal={previousTotal}
                    isPreviousPeriod={isPreviousPeriod}
                    previousPeriodStart={previousPeriodStart}
                  />
                  <Separator />
                  <div className="py-6 print:py-3">
                    <UsageChart
                      days={filteredReport?.days ?? []}
                      projectedDailyAmount={
                        isPreviousPeriod ? 0 : projection.projectedDailyAmount
                      }
                      periodStart={selectedPeriodStart}
                      periodEnd={selectedPeriodEnd}
                      highlightedType={highlightedType}
                    />
                  </div>
                  <div className="pb-6 print:py-3">
                    <UsageTable
                      groups={tableGroups}
                      totalGross={currentTotal}
                      serviceDisplayNameMap={serviceDisplayNameMap}
                      onHighlightType={setHighlightedType}
                    />
                  </div>
                </>
              ) : (
                <>
                  <EmptySummaryCards />
                  <Separator />
                  <EmptyUsageChart />
                  <div className="pb-6">
                    <EmptyUsageTable />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </CardContent>
      </Card>
      <QueryErrorBoundary
        queries={[USAGE_ALERT_CONFIG_QUERY]}
        fallback={<UsageAlertCardFallback />}
      >
        <UsageAlertCard
          scopeLabel="Organization"
          currentSpendUsd={alertSpendUsd}
          periodEnd={currentPeriodEnd}
        />
      </QueryErrorBoundary>
    </div>
  )
}
